import React from 'react';
import PropTypes from 'prop-types';
import { configureForm } from '@thinkmill/pragmatic-forms';
import { Helmet } from 'react-helmet';

import { encode } from 'inside-out/managers/formManager';
import { HeaderManager } from 'inside-out/managers/headerManager';
import { format } from 'inside-out/managers/dateManager';

import { FileAttachments } from 'inside-out/components/shared/fileAttachments';
import { Link } from 'inside-out/components/shared/link';
import { DropZone } from 'inside-out/components/shared/dropZone';

import {
	innerWrapperStyle,
	titleStyle,
	bodyStyle,
	contactTitleStyle,
	innerStyleOrganisation,
	subTitleStyle,
	locationStyle,
	metaStyle,
} from 'inside-out/page-styles/employment/detail';

import {
	aqua,
	button,
	detailSideBarLayout,
	contentSection,
	contactList,
	formSection,
} from 'inside-out/global-styles';

// Gatsby requires a specific export format on Templates.
export default class EmploymentDetail extends React.PureComponent {
	static propTypes = {
		data: PropTypes.shape({
			position: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							title: PropTypes.string.isRequired,
							type: PropTypes.string.isRequired,
							organisation_logo: PropTypes.string.isRequired,
							organisation: PropTypes.string.isRequired,
							location: PropTypes.string.isRequired,
							supervisor: PropTypes.string.isRequired,
							recruitment_close_date: PropTypes.string.isRequired,
							hours_per_week: PropTypes.string.isRequired,
							position_description: PropTypes.string.isRequired,
							attachments: PropTypes.arrayOf(
								PropTypes.shape({
									button_text: PropTypes.string,
									file: PropTypes.string,
								})
							),
							contact_first_name: PropTypes.string.isRequired,
							contact_last_name: PropTypes.string.isRequired,
							contact_phone_country_code: PropTypes.string.isRequired,
							contact_phone: PropTypes.string.isRequired,
							contact_email: PropTypes.string.isRequired,
							contact_website: PropTypes.string.isRequired,
						}),
					})
				),
			}),
		}),
	};

	state = {
		hasSubmitted: false,
	};

	ApplyNowForm = configureForm({
		initFields: () => ({
			'form-name': 'apply-job',
			botField: '',
			firstName: '',
			lastName: '',
			email: '',
			phoneNumberPrefix: '+61',
			phoneNumber: '',
			howContribute: '',
			cv: null,
		}),
		validate: ({
			firstName,
			lastName,
			email,
			phoneNumberPrefix,
			phoneNumber,
			howContribute,
			cv,
		}) => {
			const errors = {};
			if (!firstName) errors.firstName = 'Please enter your First Name';
			if (!lastName) errors.lastName = 'Please enter your Last Name';
			if (!email) errors.email = 'Please enter an Email Address';
			if (!phoneNumberPrefix) errors.phoneNumberPrefix = 'Please enter a phone prefix';
			if (!phoneNumber) errors.phoneNumber = 'Please enter a phone number';
			if (!howContribute) errors.howContribute = 'Please enter how you would like contribute';
			if (!cv) errors.cv = 'Please provide your C.V.';
			return errors;
		},
		submit: async formData => {
			try {
				await fetch('/', {
					method: 'POST',
					body: encode(formData),
				});
				this.setState({ hasSubmitted: true });
			} catch (error) {
				console.error(error);
			}
		},
	})(({ form }) => (
		<form.Form netlify="true" name="apply-job" netlify-honeypot="botField">
			<div className={formSection.wrapper}>
				<p style={{ display: 'none' }}>
					<label>
						Don’t fill this out if you&apos;re human:
						<input {...form.getInputProps({ name: 'botField', type: 'text' })} />
					</label>
				</p>
				<div aria-live="assertive">
					{/* Errors that come back from the server */}
					{form.submitError && (
						<p className={formSection.validationMessage}>Error: {form.submitError}</p>
					)}
					{/* Field Validation Errors */}
					{form.hasErrors && (
						<ul className={formSection.validationSummary}>
							{Object.keys(form.errors).map(key => (
								<li key={key} className={formSection.validationMessage}>{`${form.errors[key]}`}</li>
							))}
						</ul>
					)}
				</div>
				<div className={formSection.halfWidthWrapper}>
					<div className={formSection.field}>
						{form.errors['firstName'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['firstName']}</p>
						)}
						<label htmlFor="firstName">First Name</label>
						<input id="firstName" {...form.getInputProps({ name: 'firstName', type: 'text' })} />
					</div>

					<div className={formSection.field}>
						{form.errors['lastName'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['lastName']}</p>
						)}
						<label htmlFor="lastName">Last Name</label>
						<input id="lastName" {...form.getInputProps({ name: 'lastName', type: 'text' })} />
					</div>
				</div>

				<div className={formSection.field}>
					{form.errors['email'] && (
						<p className={formSection.validationFieldMessage}>{form.errors['email']}</p>
					)}
					<label htmlFor="emailAddress">Email Address</label>
					<input id="emailAddress" {...form.getInputProps({ name: 'email', type: 'text' })} />
				</div>

				<div className={formSection.phoneField}>
					{form.errors['phoneNumberPrefix'] && (
						<p className={formSection.validationFieldMessage}>{form.errors['phoneNumberPrefix']}</p>
					)}
					{form.errors['phoneNumber'] && (
						<p className={formSection.validationFieldMessage}>{form.errors['phoneNumber']}</p>
					)}
					<label htmlFor="phoneNumberPrefix">Phone Number</label>
					<select
						id="phoneNumberPrefix"
						name="phoneNumberPrefix"
						{...form.getInputProps({ name: 'phoneNumberPrefix', type: 'text' })}
					>
						<option value="+61">+61</option>
					</select>
					<input id="phoneNumber" {...form.getInputProps({ name: 'phoneNumber', type: 'text' })} />
				</div>

				<div className={formSection.field}>
					{form.errors['howContribute'] && (
						<p className={formSection.validationFieldMessage}>{form.errors['howContribute']}</p>
					)}
					<label htmlFor="howContribute">
						Tell us how you would like to contribute to our work at Inside Out
					</label>
					<textarea id="howContribute" {...form.getInputProps({ name: 'howContribute' })} />
				</div>

				<div className={formSection.field}>
					{form.errors['cv'] && (
						<p className={formSection.validationFieldMessage}>{form.errors['cv']}</p>
					)}
					<label htmlFor="cv">Drop your CV here</label>

					<DropZone
						className={formSection.dropZoneField.default}
						{...form.getInputProps({ name: 'cv', type: 'file' })}
					>
						{(file, clearFile) => {
							if (file) {
								return (
									<p>
										<button onClick={clearFile}>Clear</button>
										<span>{file.name}</span>
									</p>
								);
							} else {
								return (
									<p>
										<span>Drop your CV here or</span>
										<span className={button(aqua)}>Choose a file to upload</span>
									</p>
								);
							}
						}}
					</DropZone>
				</div>

				<button
					className={formSection.submitButton}
					type="submit"
					disabled={form.hasErrors || form.isLoading}
				>
					Apply Now
				</button>
			</div>
		</form.Form>
	));

	componentDidMount = () => {
		HeaderManager.headerProperties = {};
	};

	render() {
		const { node } = this.props.data.position.edges[0];

		return (
			<section>
				<Helmet title={ node.title }>
					<meta property="og:type" content="website"/>
			    <meta property="og:title" content={ node.title }/>
				</Helmet>
				<div className={innerWrapperStyle}>
					<div className={detailSideBarLayout.wrapper}>
						<div className={detailSideBarLayout.leftSide}>
							<div className={contentSection('#fff')}>
								<h1 className={titleStyle}>{node.title}</h1>
								<p className={locationStyle}>@ {node.organisation}</p>
								<div className={metaStyle}>
									<table>
										<tbody>
											<tr>
												<th>
													<span>Location</span>
												</th>
												<th>
													<span>Hours per week</span>
												</th>
												<th>
													{node.recruitment_close_date && (
															<span>Closing Date</span>
													)}
												</th>
											</tr>
											<tr>
												<td>
													<span>{node.location}</span>
												</td>
												<td>
													<span>{node.hours_per_week}</span>
												</td>
												<td>
													{node.recruitment_close_date && (
														<span>{format(node.recruitment_close_date)}</span>
													)}
												</td>
											</tr>
										</tbody>
									</table>
									<table>
										<tbody>
											<tr>
												<th>
													<span>Location</span>
												</th>
												<td>
													<span>{node.location}</span>
												</td>
											</tr>
											<tr>
												<th>
													<span>Hours per week</span>
												</th>
												<td>
													<span>{node.hours_per_week}</span>
												</td>
											</tr>
											<tr>
												<th>
													<span>Closing Date</span>
												</th>
												<td>
													<span>{format(node.recruitment_close_date)}</span>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
								<h2 className={subTitleStyle}>About the role</h2>
								<div
									className={bodyStyle}
									dangerouslySetInnerHTML={{ __html: node.position_description }}
								/>

								<FileAttachments title="Attachments" attachments={node.attachments} />
							</div>
						</div>
						<div className={detailSideBarLayout.rightSide}>
							{node.organisation_logo && (
								<div className={contentSection('#fff')}>
									<div className={innerStyleOrganisation}>
										<img src={node.organisation_logo} alt="" />
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				<div className={contentSection()}>
					<div className={innerWrapperStyle}>
						<div className={detailSideBarLayout.wrapper}>
							<div className={detailSideBarLayout.leftSide}>
								<h2 className={contactTitleStyle}>
									{node.contact_first_name} {node.contact_last_name}
								</h2>
								<ul className={contactList.wrapper}>
									{node.contact_phone && (
										<li className={contactList.item('phone-number')}>{node.contact_phone}</li>
									)}
									{node.contact_email && (
										<li className={contactList.item('email')}>
											<Link href={`mailto:${node.contact_email}`}>
												{node.contact_email}
											</Link>
										</li>
									)}
									{node.contact_website && (
										<li className='fullWidth'>
											<Link
												className={formSection.submitButton}
												href={node.contact_website.indexOf('://') === -1 ? `http://${node.contact_website}` : node.contact_website}
											>
												Go to website and apply
											</Link>
										</li>
									)}
								</ul>
							</div>
						</div>
					</div>
				</div>

				{node.organisation === 'InsideOut' && (
					<div className={contentSection('#fff')}>
						<div className={innerWrapperStyle}>
							<div className={detailSideBarLayout.wrapper}>
								<div className={detailSideBarLayout.leftSide}>
									{!this.state.hasSubmitted && <this.ApplyNowForm />}
									{this.state.hasSubmitted && (
										<React.Fragment>
											<p className={titleStyle}>Thank you.</p>
										</React.Fragment>
									)}
								</div>
							</div>
						</div>
					</div>
				)}
			</section>
		);
	}
}

export const query = graphql`
	query EmploymentDetailQuery($id: String!) {
		position: allEmploymentJson(filter: { id: { eq: $id } }) {
			edges {
				node {
					title
					type
					organisation_logo
					organisation
					location
					hours_per_week
					recruitment_close_date
					position_description
					# attachments {
					# 	button_text
					# 	file
					# }
					contact_first_name
					contact_last_name
					contact_phone
					contact_email
					contact_website
				}
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/templates/employment/detail/index.js